import { truncateAddress } from '@kwenta/sdk/utils'
import { getAddress, isAddress } from 'ethers/lib/utils.js'
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import PencilIcon from 'assets/svg/app/pencil.svg'
import DelegationIcon from 'assets/svg/futures/delegation-icon.svg'
import MoreIcon from 'assets/svg/futures/more-icon.svg'
import NoDataIcon from 'assets/svg/futures/no-data.svg'
import RemoveIcon from 'assets/svg/futures/remove-icon.svg'
import SelectedAccountIcon from 'assets/svg/futures/selected-account-icon.svg'
import { TransactionButton } from 'components/Button/Button'
import TabButton from 'components/Button/TabButton'
import { notifyError } from 'components/ErrorNotifier'
import Input from 'components/Input/Input'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import useClickOutside from 'hooks/useClickOutside'
import useENS from 'hooks/useENS'
import { setOpenModal } from 'state/app/reducer'
import { fetchDelegatesForAccount, fetchSubAccountsForAccount } from 'state/futures/actions'
import { selectSelectedSubAccountAddress } from 'state/futures/common/selectors'
import { setSelectedDelegationAddress, setSelectedSubAccountAddress } from 'state/futures/reducer'
import {
	selectAddressBook,
	selectDelegatesList,
	selectSelectedDelegationAddress,
	selectSubAccountList,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import media from 'styles/media'

import { SectionSeparator } from '../mobile'

export enum DelegationTab {
	Delegations = 'delegations',
	SubAccounts = 'sub-accounts',
}

type Props = {
	defaultTab: DelegationTab
}

const DelegationTradingTab: FC<Props> = memo(({ defaultTab }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const selectedDelegationAddress = useAppSelector(selectSelectedDelegationAddress)
	const selectedSubAccountAddress = useAppSelector(selectSelectedSubAccountAddress)
	const delegatesList = useAppSelector(selectDelegatesList)
	const subAccountList = useAppSelector(selectSubAccountList)
	const addressBook = useAppSelector(selectAddressBook)
	const [currentTab, setCurrentTab] = useState(defaultTab)
	const [addressOrName, setAddressOrName] = useState<string>('')
	const [menuOpen, setMenuOpen] = useState(false)
	const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null)
	const { ensAddress } = useENS(addressOrName)
	const newDelegateAddress = useMemo(() => {
		const address = ensAddress || addressOrName
		return isAddress(ensAddress || addressOrName) ? getAddress(address) : address
	}, [addressOrName, ensAddress])
	const { ref } = useClickOutside(() => setMenuOpen(false))

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setAddressOrName(e.target.value),
		[]
	)

	const handleChangeTab = useCallback(
		(tab: DelegationTab) => () => {
			setCurrentTab(tab)
		},
		[]
	)

	const handleAddDelegate = useCallback(() => {
		const isAddressExist = delegatesList.some(({ address }) => address === newDelegateAddress)
		if (!isAddressExist) {
			dispatch(setSelectedDelegationAddress(newDelegateAddress))
			dispatch(setOpenModal('add_delegate'))
			setMenuOpen(false)
		} else {
			notifyError('This address already exists in your list')
		}
	}, [delegatesList, dispatch, newDelegateAddress])

	const handleManage = useCallback(
		(event: React.MouseEvent, address: string, type: 'delegate' | 'subaccount') => {
			const x = event.clientX
			const y = event.clientY
			dispatch(
				type === 'delegate'
					? setSelectedDelegationAddress(address)
					: setSelectedSubAccountAddress(address)
			)
			setMenuPosition({ x, y })
			setMenuOpen(true)
		},
		[dispatch]
	)

	useEffect(() => {
		dispatch(fetchDelegatesForAccount())
		dispatch(fetchSubAccountsForAccount())
	}, [addressBook, dispatch])

	const handleRemoveDelegate = useCallback(() => {
		if (selectedDelegationAddress) {
			dispatch(setOpenModal('remove_delegate'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a delegate to remove')
		}
	}, [dispatch, selectedDelegationAddress])

	const handleRenameDelegate = useCallback(() => {
		if (selectedDelegationAddress) {
			dispatch(setOpenModal('rename_delegate'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a delegate to rename')
		}
	}, [dispatch, selectedDelegationAddress])

	const handleRenameSubAccount = useCallback(() => {
		if (selectedSubAccountAddress) {
			dispatch(setOpenModal('rename_subaccount'))
			setMenuOpen(false)
		} else {
			notifyError('Please select a subaccount to rename')
		}
	}, [dispatch, selectedSubAccountAddress])

	const handleSelectedSubAccount = useCallback(
		(address: string) => {
			dispatch(setSelectedSubAccountAddress(address))
		},
		[dispatch]
	)

	return (
		<>
			<TabButtons>
				<TabButton
					variant="noOutline"
					title={t('futures.market.trade.margin.modal.delegation.delegations-tabs')}
					onClick={handleChangeTab(DelegationTab.Delegations)}
					active={currentTab === DelegationTab.Delegations}
				/>
				<TabButton
					variant="noOutline"
					title={t('futures.market.trade.margin.modal.delegation.sub-accounts-tabs')}
					onClick={handleChangeTab(DelegationTab.SubAccounts)}
					active={currentTab === DelegationTab.SubAccounts}
				/>
			</TabButtons>
			<Container>
				<TabPanel name={DelegationTab.Delegations} activeTab={currentTab}>
					<FlexDivCol rowGap="20px">
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.delegation.add-new-delegate')}
						</Body>
						<StyledInput value={newDelegateAddress} onChange={onChange} placeholder="0x" />
						<TransactionButton
							fullWidth
							disabled={!isAddress(newDelegateAddress)}
							onClick={handleAddDelegate}
						>
							{t('futures.market.trade.margin.modal.delegation.add-delegate-button')}
						</TransactionButton>
						<StyledSectionSeparator />
						<Body>{t('futures.market.trade.margin.modal.delegation.manage-delegations')}</Body>
						<ContainerWithInfiniteScroll rowGap="30px">
							{delegatesList.length === 0 ? (
								<FlexDivColCentered rowGap="20px">
									<NoDataIcon />
									<Body color="secondary">
										{t('futures.market.trade.margin.modal.delegation.no-delegations')}
									</Body>
								</FlexDivColCentered>
							) : (
								delegatesList.map(({ address, nickname }, index) => (
									<FlexDivRowCentered key={index} justifyContent="space-between">
										<FlexDivRow justifyContent="flexStart" columnGap="12px">
											<DelegationIcon height={32} width={32} />
											<FlexDivCol rowGap="5px">
												<Body weight="bold" color="primary">
													{nickname ||
														t('futures.market.trade.margin.modal.delegation.default-name')}
												</Body>
												<Body color="secondary">{truncateAddress(address)}</Body>
											</FlexDivCol>
										</FlexDivRow>
										<StyeldMoreIcon
											height={24}
											width={24}
											onClick={(event: React.MouseEvent) =>
												handleManage(event, address, 'delegate')
											}
										/>
									</FlexDivRowCentered>
								))
							)}
						</ContainerWithInfiniteScroll>
					</FlexDivCol>
				</TabPanel>
				<TabPanel name={DelegationTab.SubAccounts} activeTab={currentTab}>
					<FlexDivCol rowGap="20px">
						<Body>{t('futures.market.trade.margin.modal.delegation.manage-sub-accounts')}</Body>
						<ContainerWithInfiniteScroll rowGap="30px" height="440px">
							{subAccountList.length === 0 ? (
								<NoDataContainer rowGap="30px">
									<NoDataIcon />
									<Body>{t('futures.market.trade.margin.modal.delegation.no-sub-accounts')}</Body>
								</NoDataContainer>
							) : (
								subAccountList.map(({ address, nickname }) => (
									<SubAccountRowContainer
										justifyContent="space-between"
										onClick={() => handleSelectedSubAccount(address)}
									>
										<FlexDivRow justifyContent="flexStart" columnGap="12px">
											{address === selectedSubAccountAddress ? (
												<SelectedAccountIcon height={32} width={32} />
											) : (
												<DelegationIcon height={32} width={32} />
											)}
											<FlexDivCol rowGap="5px">
												<Body weight="bold" color="primary">
													{nickname ||
														t('futures.market.trade.margin.modal.delegation.default-account-name')}
												</Body>
												<Body color="secondary">{truncateAddress(address)}</Body>
											</FlexDivCol>
										</FlexDivRow>
										<StyeldMoreIcon
											height={24}
											width={24}
											onClick={(event: React.MouseEvent) =>
												handleManage(event, address, 'subaccount')
											}
										/>
									</SubAccountRowContainer>
								))
							)}
						</ContainerWithInfiniteScroll>
					</FlexDivCol>
				</TabPanel>
				{menuOpen && menuPosition && (
					<MenuContainer
						ref={ref}
						top={`${menuPosition.y + 20}px`}
						left={`${menuPosition.x - 150}px`}
					>
						<FlexDivCol>
							<MenuItem
								columnGap="8px"
								justifyContent="flexStart"
								onClick={
									currentTab === DelegationTab.Delegations
										? handleRenameDelegate
										: handleRenameSubAccount
								}
							>
								<StyledPencilIcon height={12} width={12} />
								<Body color="primary">
									{t('futures.market.trade.margin.modal.delegation.rename.menu-title')}
								</Body>
							</MenuItem>
							{currentTab === DelegationTab.Delegations && (
								<MenuItem columnGap="8px" justifyContent="flexStart" onClick={handleRemoveDelegate}>
									<RemoveIcon />
									<Body color="negative">
										{t('futures.market.trade.margin.modal.delegation.remove.menu-title')}
									</Body>
								</MenuItem>
							)}
						</FlexDivCol>
					</MenuContainer>
				)}
			</Container>
		</>
	)
})

const Container = styled.div``

const StyledPencilIcon = styled(PencilIcon)`
	fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.tertiary};
`
const NoDataContainer = styled(FlexDivColCentered)`
	margin: 50px 0;
`

const SubAccountRowContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
`

const ContainerWithInfiniteScroll = styled(FlexDivCol)<{ height?: string }>`
	height: ${(props) => props.height || '250px'};
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: transparent transparent;

	&::-webkit-scrollbar {
		width: 0;
	}
`

const MenuItem = styled(FlexDivRowCentered)`
	min-width: 160px;
	:hover {
		background: ${(props) => props.theme.colors.selectedTheme.newTheme.pill.gray.background};
	}
	padding: 9px 15px;
	cursor: pointer;
`

const MenuContainer = styled.div<{ top: string; left: string }>`
	position: fixed;
	top: ${(props) => props.top};
	left: ${(props) => props.left};
	border-radius: 8px;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	box-shadow: ${(props) => props.theme.colors.selectedTheme.button.primary.textShadow};
	padding: 8px 0px;
`

const StyeldMoreIcon = styled(MoreIcon)`
	cursor: pointer;
	:hover {
		border-radius: 100px;
		border: 1px solid
			${(props) => props.theme.colors.selectedTheme.newTheme.pill.gray.outline.border};
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.button.default.hover.background};
	}
`

const StyledSectionSeparator = styled(SectionSeparator)`
	margin: 10px 0;
`

const StyledInput = styled(Input)`
	font-size: 15px;
	font-family: ${(props) => props.theme.fonts.mono};

	${media.lessThan('md')`
		font-size: 13px;
	`}
`

const TabButtons = styled.div`
	display: flex;
	gap: 25px;
	margin-bottom: 25px;
`

export default DelegationTradingTab
